import { Alert, AlertDescription, Card, CardContent } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../../lib/api-client.js';

/**
 * Public landing page for the one-click unsubscribe link in
 * notification emails. On mount it POSTs the token to the API; we
 * render success / failure inline. No auth required — the token is
 * the proof of intent.
 */
export function PublicUnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('Missing token');
      return api<{ ok: boolean; category?: string }>(`/u/${token}`, {
        method: 'POST',
      });
    },
  });

  useEffect(() => {
    if (token) mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-muted p-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 pt-6">
          {mutation.isPending && (
            <div className="text-center text-sm text-fg-secondary">
              Updating your preferences…
            </div>
          )}
          {mutation.isSuccess && mutation.data?.ok ? (
            <>
              <h1 className="text-lg font-semibold">You're unsubscribed</h1>
              <p className="text-sm text-fg-secondary">
                You will no longer receive {mutation.data.category?.replaceAll('_', ' ') ?? 'these'} emails from SiteWise.
                You can change this any time from your notification settings.
              </p>
            </>
          ) : null}
          {(mutation.isError || (mutation.isSuccess && !mutation.data?.ok)) && (
            <Alert variant="danger">
              <AlertDescription>
                This unsubscribe link is invalid or expired. Please update your preferences from inside SiteWise.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
